import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, ChevronRight } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import SectionHeading from "@/components/SectionHeading";
import Reveal from "@/components/Reveal";
import FaqAccordion from "@/components/FaqAccordion";
import CTASection from "@/components/CTASection";
import { useJsonLd } from "@/hooks/useJsonLd";
import { SERVICES } from "@/data/site";
import { getLocation, areasForCity, AREA_LABELS } from "@/data/areas";

const LocationDetail = () => {
  const { slug } = useParams();
  const loc = getLocation(slug);

  useEffect(() => {
    document.title = loc
      ? `Digital Marketing & Web Services in ${loc.name} — Click World Online`
      : "Areas We Serve — Click World Online";
  }, [loc]);

  useJsonLd(
    "faq-jsonld",
    loc && {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: loc.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    }
  );

  if (!loc) {
    return (
      <section className="bg-navy-deep min-h-screen flex items-center">
        <div className="max-w-8xl mx-auto px-6 lg:px-12 py-40 text-center w-full">
          <h1 className="font-display text-4xl md:text-6xl font-extrabold text-mist tracking-tight">Location not found.</h1>
          <Link to="/locations" data-testid="location-not-found-back" className="mt-8 inline-flex items-center gap-2 text-electric font-semibold">
            View areas we serve <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>
    );
  }

  const combos = areasForCity(loc.slug);

  return (
    <>
      <PageHeader
        eyebrow="Area We Serve"
        title={<>Digital growth, delivered in <span className="text-electric">{loc.name}.</span></>}
        text={loc.intro}
        breadcrumb={[{ label: "Home", to: "/" }, { label: "Areas We Serve", to: "/locations" }, { label: loc.name }]}
      />

      <section className="bg-navy-deep" data-testid="location-services-section">
        <div className="max-w-8xl mx-auto px-6 lg:px-12 py-20 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7">
              <SectionHeading eyebrow={`Services in ${loc.name}`} title={<>What we deliver in <span className="text-electric">{loc.name}.</span></>} />
              <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {SERVICES.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/services/${s.slug}`}
                    data-testid={`location-service-${s.slug}`}
                    className="group flex items-center justify-between gap-3 bg-navy-light border border-white/10 rounded-2xl px-5 py-4 hover:border-electric/40 transition-colors duration-300"
                  >
                    <span className="text-mist/85 text-sm font-medium">{s.title}</span>
                    <ChevronRight size={16} className="text-mist/30 group-hover:text-electric shrink-0 transition-colors" />
                  </Link>
                ))}
              </div>
              {combos.length > 0 && (
                <Reveal className="mt-10">
                  <h3 className="font-display text-xl font-extrabold text-mist tracking-tight">Popular in {loc.name}</h3>
                  <div className="mt-4 flex flex-wrap gap-3">
                    {combos.map((c) => (
                      <Link
                        key={c.service}
                        to={`/${c.service}/${c.city}`}
                        data-testid={`location-combo-${c.service}`}
                        className="border border-electric/30 text-electric rounded-full px-5 py-2.5 text-sm font-medium hover:bg-electric/10 transition-colors duration-300"
                      >
                        {AREA_LABELS[c.service]} in {loc.name}
                      </Link>
                    ))}
                  </div>
                </Reveal>
              )}
            </div>

            <div className="lg:col-span-5">
              <Reveal delay={0.1}>
                <div className="bg-navy-light border border-white/10 rounded-3xl p-8 lg:p-10 grain relative overflow-hidden lg:sticky lg:top-28">
                  <div className="absolute inset-0 dot-grid opacity-15" aria-hidden="true" />
                  <div className="relative">
                    <h3 className="font-display text-xl font-extrabold text-mist tracking-tight">Businesses we help in {loc.name}</h3>
                    <div className="mt-5 flex flex-wrap gap-2">
                      {loc.industries.map((ind) => (
                        <span key={ind} className="text-sm text-mist/80 border border-white/10 bg-white/5 rounded-full px-4 py-2">
                          {ind}
                        </span>
                      ))}
                    </div>
                    <div className="mt-6 pt-6 border-t border-white/10">
                      <p className="text-slatec text-sm leading-relaxed">{loc.marketNote}</p>
                    </div>
                    <Link
                      to="/contact"
                      data-testid="location-cta"
                      className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-brand hover:bg-electric hover:text-navy-deep text-white font-semibold px-8 py-4 rounded-full transition-colors duration-300"
                    >
                      Discuss your {loc.name} business <ArrowUpRight size={18} />
                    </Link>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-mist text-navy dot-grid-light" data-testid="location-faq-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-20 lg:py-32">
          <SectionHeading dark={false} eyebrow="FAQs" title={<>Working with us <span className="text-brand">in {loc.name}.</span></>} />
          <Reveal className="mt-12">
            <FaqAccordion items={loc.faqs} idPrefix="location-faq" />
          </Reveal>
        </div>
      </section>

      <CTASection title={`Let's grow your ${loc.name} business.`} />
    </>
  );
};

export default LocationDetail;
